import React, { useState, useEffect } from 'react';
import { 
  UsersIcon, 
  TrophyIcon, 
  CurrencyRupeeIcon, 
  ArrowTrendingUpIcon,
  UserGroupIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  FireIcon
} from '@heroicons/react/24/outline';
import adminApi from '../../api/adminApi';
import { useAuth } from '../../contexts/AuthContext';
import Toast from '../../components/ui/Toast';

const Dashboard = () => {
  const { user: adminUser } = useAuth();
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeCompetitions: 0,
    totalRevenue: 0,
    todayRegistrations: 0,
    pendingPayments: 0,
    activeTeams: 0,
    scheduledDeletions: 0
  });
  const [recentUsers, setRecentUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);

  const showToast = (msg, type = 'success') => {
    setNotification({ msg, type });
  };

  const fetchDashboardData = async () => {
  setLoading(true);
  try {
    const data = await adminApi.getDashboardSummary();
    
    setStats({
      totalUsers: data.totalUsers || 0,
      activeCompetitions: data.activeCompetitions || 0,
      totalRevenue: data.totalRevenue || 0, 
      todayRegistrations: data.todayRegistrations || 0,
      pendingPayments: data.pendingPayments || 0,
      activeTeams: data.activeTeams || 0,
      scheduledDeletions: data.scheduledDeletions || 0
    });
    
    const usersData = await adminApi.getRecentUsers();
    setRecentUsers(usersData || []);
  } catch (error) {
    showToast("Failed to sync dashboard stats", "error");
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const StatCard = ({ title, value, icon: Icon, color, trend }) => (
    <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6 hover:border-gray-600 transition-all hover:scale-[1.02] duration-200">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg ${color} shadow-lg`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        {trend && (
          <div className={`flex items-center text-sm font-medium ${trend > 0 ? 'text-green-400' : 'text-red-400'}`}>
            <ArrowTrendingUpIcon className={`h-4 w-4 mr-1 ${trend > 0 ? '' : 'rotate-180'}`} />
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <h3 className="text-3xl font-bold text-white mb-2">
        {loading ? <div className="h-8 w-20 bg-gray-700 rounded animate-pulse"></div> : value}
      </h3>
      <p className="text-gray-400">{title}</p>
    </div>
  );

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4">
      {notification && (
        <Toast 
          message={notification.msg} 
          type={notification.type} 
          onClose={() => setNotification(null)} 
        />
      )}

      <div className="bg-gradient-to-r from-gray-800 to-gray-900 border border-gray-700 rounded-2xl p-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-l from-blue-500/10 to-purple-500/10 rounded-full blur-3xl -translate-y-32 translate-x-32"></div>
        <div className="relative z-10 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white mb-2 flex items-center">
              Welcome back, 
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400 ml-2">
                {(adminUser?.name || 'Admin').split(' ')[0]}
              </span> 👋
            </h1>
            <p className="text-gray-400">System surveillance active.</p>
          </div>
          <button onClick={fetchDashboardData} className="flex items-center space-x-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg border border-gray-700 transition-colors">
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <StatCard title="Total Users" value={stats.totalUsers} icon={UsersIcon} color="bg-gradient-to-r from-blue-600 to-cyan-600" trend={12} />
        <StatCard title="Active Competitions" value={stats.activeCompetitions} icon={TrophyIcon} color="bg-gradient-to-r from-purple-600 to-pink-600" trend={8} />
        <StatCard title="Today's Registrations" value={stats.todayRegistrations} icon={UserGroupIcon} color="bg-gradient-to-r from-green-600 to-emerald-600" />
        <StatCard title="Total Revenue" value={`₹${stats.totalRevenue.toLocaleString('en-IN')}`} icon={CurrencyRupeeIcon} color="bg-gradient-to-r from-yellow-600 to-orange-600" trend={25} />
        <StatCard title="Pending Payments" value={stats.pendingPayments} icon={ClockIcon} color="bg-gradient-to-r from-red-600 to-rose-600" />
        <StatCard title="Active Teams" value={stats.activeTeams} icon={CheckCircleIcon} color="bg-gradient-to-r from-indigo-600 to-violet-600" trend={18} />
      </div>

      <div className="bg-gradient-to-r from-red-900/20 to-orange-900/20 border border-red-500/30 rounded-2xl p-6 shadow-2xl">
        <h2 className="text-xl font-bold text-white flex items-center gap-2 mb-4">
          <FireIcon className="h-6 w-6 text-red-500" /> Deletion Queue (5-Day Window)
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-900/50 p-4 rounded-xl border border-gray-700">
            <p className="text-gray-400 text-sm font-medium">Users Scheduled</p>
            <p className="text-2xl font-bold text-red-400">{loading ? "..." : stats.scheduledDeletions}</p>
          </div>
          <div className="bg-gray-900/50 p-4 rounded-xl border border-gray-700">
            <p className="text-gray-400 text-sm font-medium">Automated Cleanup</p>
            <p className="text-white flex items-center">Daily at 2:00 AM</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;